// ── Quick-Buy Controller ──
// MOB-W6: customizable quick-buy buttons for the mobile HUD. Three slots,
// each mapped to a user-chosen weapon, persisted to localStorage. The
// controller exposes a triggerBuy(slot) entry that hands the chosen weapon
// id off to the host's existing buy handler so the wire path matches the
// desktop buy flow exactly.
//
// Defaults are team-aware:
//   T  → ak47 / awp / deagle
//   CT → m4a4 / awp / deagle
// matching the BuyMenu's typical "first round upgrade" path so a fresh
// install has sensible buttons before the player customizes them.

import { WEAPONS, type WeaponId } from '../core/config';

/** localStorage key for the saved loadout. */
export const QUICK_BUY_STORAGE_KEY = 'cp-quick-buy-loadout';

/** Number of quick-buy slots (also the length of any valid loadout). */
export const QUICK_BUY_SLOTS = 3;

/** Per-team defaults applied when no saved loadout exists. */
export const DEFAULT_T_LOADOUT: WeaponId[] = ['ak47', 'awp', 'deagle'];
export const DEFAULT_CT_LOADOUT: WeaponId[] = ['m4a4', 'awp', 'deagle'];

export type QuickBuyTeam = 'T' | 'CT';
export type QuickBuySlot = 0 | 1 | 2;

/**
 * BUG-W-7: Team restriction lookup for weapons. Mirrors the server's
 * `WeaponTeamRestriction` map in `server/internal/game/economy.go` so the
 * client can pre-empt a buy that the server would reject — otherwise the
 * mobile quick-buy deducts money optimistically and the UI flashes a
 * deduction that snaps back when the server returns a refund. Only the
 * actually-restricted weapons are listed; everything else is shared and
 * does not need an entry.
 */
export const QUICK_BUY_WEAPON_TEAM: Partial<Record<WeaponId, QuickBuyTeam>> = {
  // T-only
  glock: 'T',
  ak47: 'T',
  mac10: 'T',
  galil: 'T',
  sg553: 'T',
  tec9: 'T',
  // CT-only
  usp: 'CT',
  m4a4: 'CT',
  m4a1s: 'CT',
  mp9: 'CT',
  famas: 'CT',
  aug: 'CT',
  fiveseven: 'CT',
};

/**
 * Returns true when the given weapon is restricted to the *other* team
 * (i.e. the local player cannot buy it on their current team). Shared
 * weapons (knife, awp, deagle, …) always return false.
 */
export function isWeaponRestrictedForTeam(
  weaponId: WeaponId,
  team: QuickBuyTeam,
): boolean {
  const weaponTeam = QUICK_BUY_WEAPON_TEAM[weaponId];
  return Boolean(weaponTeam && weaponTeam !== team);
}

export interface QuickBuyControllerOptions {
  /** Player's current team — picks the default loadout when none is saved. */
  team: QuickBuyTeam;
  /** Buy handler — called with a weapon id when triggerBuy fires. Must
   *  match the same path used by the desktop BuyMenu's onBuy callback so
   *  the server sees identical buy messages. */
  triggerBuy: (weaponId: WeaponId) => void;
  /** BUG-W-25 / long-press overwrite: returns the player's currently held
   *  weapon, used when the user long-presses a slot to replace it with what
   *  they're holding. Returning null aborts the dialog (no weapon to save). */
  getCurrentWeapon?: () => WeaponId | null;
}

/** BUG-W-25: CustomEvent name dispatched on `document` when localStorage
 *  writes fail (iOS private mode, quota exceeded, disabled, etc.) so the
 *  toast system can surface the failure to the user. */
export const QUICK_BUY_STORAGE_FAILED_EVENT = 'quick-buy-storage-failed';

function defaultsForTeam(team: QuickBuyTeam): WeaponId[] {
  return team === 'T' ? [...DEFAULT_T_LOADOUT] : [...DEFAULT_CT_LOADOUT];
}

/**
 * Coerce raw stored values into a valid loadout. Unknown ids fall back to
 * the per-team default for that slot. Returns null if the stored payload
 * is the wrong shape (length mismatch / not an array) so the caller can
 * treat that as "no saved loadout" and re-emit defaults.
 */
function sanitizeLoadout(
  raw: unknown,
  team: QuickBuyTeam,
): WeaponId[] | null {
  if (!Array.isArray(raw)) return null;
  if (raw.length !== QUICK_BUY_SLOTS) return null;
  const fallback = defaultsForTeam(team);
  const out: WeaponId[] = [];
  for (let i = 0; i < QUICK_BUY_SLOTS; i++) {
    const candidate = raw[i];
    if (typeof candidate === 'string' && candidate in WEAPONS) {
      out.push(candidate as WeaponId);
    } else {
      out.push(fallback[i]);
    }
  }
  return out;
}

function readStored(team: QuickBuyTeam): WeaponId[] | null {
  try {
    const raw = localStorage.getItem(QUICK_BUY_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return sanitizeLoadout(parsed, team);
  } catch {
    return null;
  }
}

export class QuickBuyController {
  private team: QuickBuyTeam;
  private triggerBuyHandler: (weaponId: WeaponId) => void;
  private getCurrentWeapon: (() => WeaponId | null) | undefined;
  private loadout: WeaponId[];
  /** BUG-W-25: only warn + dispatch once per session so a broken
   *  localStorage backend doesn't spam the console or toast queue. */
  private storageFailureNotified = false;
  /** Track the active overwrite dialog so we never stack two on top of each
   *  other (a fast double-long-press on different slots would otherwise
   *  leave orphan overlays behind). */
  private activeOverwriteDialog: HTMLElement | null = null;

  constructor(opts: QuickBuyControllerOptions) {
    this.team = opts.team;
    this.triggerBuyHandler = opts.triggerBuy;
    this.getCurrentWeapon = opts.getCurrentWeapon;
    this.loadout = readStored(opts.team) ?? defaultsForTeam(opts.team);
  }

  /** Read the current loadout. Always exactly QUICK_BUY_SLOTS entries. */
  getLoadout(): WeaponId[] {
    return [...this.loadout];
  }

  /** Persist a new loadout to localStorage. Unknown ids are coerced back to
   *  the team default for that slot so callers can pass partial / typo'd
   *  payloads without blowing up the store. */
  saveLoadout(next: readonly WeaponId[]): void {
    const sanitized = sanitizeLoadout([...next], this.team);
    if (!sanitized) return;
    this.loadout = sanitized;
    try {
      localStorage.setItem(QUICK_BUY_STORAGE_KEY, JSON.stringify(sanitized));
    } catch (err) {
      // BUG-W-25: localStorage may be disabled (iOS private mode throws on
      // setItem, quota exceeded, etc.). Keep the in-memory state regardless,
      // but surface the failure so the toast system can tell the user their
      // change won't survive a refresh. Warn + dispatch only once per
      // session so a persistently broken backend doesn't spam.
      this.notifyStorageFailure(err);
    }
  }

  /** BUG-W-25: one-time-per-session notification of a localStorage write
   *  failure. Console.warn for developers + CustomEvent on `document` so
   *  the toast system (or any other listener) can surface it to the user. */
  private notifyStorageFailure(err: unknown): void {
    if (this.storageFailureNotified) return;
    this.storageFailureNotified = true;
    try {
      console.warn(
        '[quick-buy] localStorage write failed — loadout will not persist across refresh',
        err,
      );
    } catch {
      // console may be missing in some embedding runtimes; safe to ignore.
    }
    try {
      if (typeof document !== 'undefined' && typeof CustomEvent !== 'undefined') {
        document.dispatchEvent(
          new CustomEvent(QUICK_BUY_STORAGE_FAILED_EVENT, {
            detail: { error: err instanceof Error ? err.message : String(err) },
          }),
        );
      }
    } catch {
      // dispatchEvent may also throw on some stub DOMs; the warn above is
      // enough on its own.
    }
  }

  /** Update the active team. If the user has no saved loadout, the team
   *  defaults swap; if they do, their override wins. */
  setTeam(team: QuickBuyTeam): void {
    this.team = team;
    const stored = readStored(team);
    if (stored) {
      this.loadout = stored;
    } else {
      this.loadout = defaultsForTeam(team);
    }
  }

  /** Fire the buy handler for the weapon at the given slot. Out-of-range
   *  slots are a no-op so we never call into the buy path with garbage.
   *
   *  BUG-W-7: If the weapon at the slot is restricted to the *other* team
   *  (e.g. a CT player saved m4a4 to slot 0, then switched to T), do not
   *  call the handler — the server would reject it anyway and the host's
   *  optimistic money deduction would flash on screen then snap back. */
  triggerBuy(slot: QuickBuySlot): void {
    if (typeof slot !== 'number' || slot < 0 || slot >= QUICK_BUY_SLOTS) return;
    const weaponId = this.loadout[slot];
    if (!weaponId) return;
    if (isWeaponRestrictedForTeam(weaponId, this.team)) return;
    this.triggerBuyHandler(weaponId);
  }

  /** BUG-W-7: returns true when the weapon assigned to the given slot
   *  cannot be bought by the local player on their current team. The
   *  mobile HUD uses this to apply a `.team-restricted` class to the
   *  matching button so the user can see the slot is unbuyable before
   *  tapping it. Out-of-range slots return false. */
  isSlotRestricted(slot: QuickBuySlot): boolean {
    if (typeof slot !== 'number' || slot < 0 || slot >= QUICK_BUY_SLOTS) return false;
    const weaponId = this.loadout[slot];
    if (!weaponId) return false;
    return isWeaponRestrictedForTeam(weaponId, this.team);
  }

  /** Short label for display on a button. We strip non-alpha characters
   *  from the weapon's canonical name so labels fit in the 56px button
   *  footprint (e.g. "AK-47" → "AK47"). */
  getButtonLabel(slot: QuickBuySlot): string {
    const weaponId = this.loadout[slot];
    if (!weaponId) return '';
    const cfg = WEAPONS[weaponId];
    if (!cfg) return weaponId.toUpperCase();
    return cfg.name.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  }

  /** Read-only accessor for the current team. */
  getTeam(): QuickBuyTeam {
    return this.team;
  }

  /** Long-press overwrite: opens a small confirm popup asking the user to
   *  replace the weapon at `slot` with the weapon they're currently holding.
   *  Wire this from the host's button long-press handler (700ms hold). On
   *  Yes, reads the current weapon via the `getCurrentWeapon` callback
   *  passed at construction time and saves it into the slot.
   *
   *  No-ops if:
   *   - the slot is out of range,
   *   - no `getCurrentWeapon` callback was supplied,
   *   - the callback returns null / an unknown weapon id,
   *   - there is already an active dialog (avoid double-open on rapid
   *     long-presses).
   */
  requestOverwrite(slot: QuickBuySlot): void {
    if (typeof slot !== 'number' || slot < 0 || slot >= QUICK_BUY_SLOTS) return;
    if (!this.getCurrentWeapon) return;
    if (this.activeOverwriteDialog) return;
    if (typeof document === 'undefined') return;

    const current = this.getCurrentWeapon();
    if (!current || !(current in WEAPONS)) return;

    this.openOverwriteDialog(slot, current);
  }

  /** Build + show the confirm popup. Kept private so the public surface
   *  area stays narrow — callers go through requestOverwrite which reads
   *  the current weapon from the supplied callback. */
  private openOverwriteDialog(slot: QuickBuySlot, weaponId: WeaponId): void {
    const overlay = document.createElement('div');
    overlay.className = 'quick-buy-overwrite-overlay';
    overlay.dataset.slot = String(slot);
    overlay.style.position = 'fixed';
    overlay.style.inset = '0';
    overlay.style.background = 'rgba(0, 0, 0, 0.55)';
    overlay.style.display = 'flex';
    overlay.style.alignItems = 'center';
    overlay.style.justifyContent = 'center';
    overlay.style.zIndex = '9999';

    const popup = document.createElement('div');
    popup.className = 'quick-buy-overwrite-popup';
    popup.style.background = '#1a1a1a';
    popup.style.color = '#e8e8e8';
    popup.style.padding = '20px 24px';
    popup.style.borderRadius = '8px';
    popup.style.minWidth = '260px';
    popup.style.maxWidth = '320px';
    popup.style.textAlign = 'center';
    popup.style.boxShadow = '0 8px 32px rgba(0,0,0,0.6)';
    popup.style.fontFamily = 'system-ui, sans-serif';

    const message = document.createElement('div');
    message.className = 'quick-buy-overwrite-message';
    // Slot is zero-indexed internally; user-visible slot number is 1-based
    // to match the btn-quickbuy-1/2/3 IDs the player sees on the HUD.
    message.textContent = `Replace Q-slot ${slot + 1} with currently held weapon?`;
    message.style.marginBottom = '16px';
    message.style.fontSize = '14px';
    popup.appendChild(message);

    const buttonRow = document.createElement('div');
    buttonRow.style.display = 'flex';
    buttonRow.style.gap = '12px';
    buttonRow.style.justifyContent = 'center';

    const yesBtn = document.createElement('button');
    yesBtn.className = 'quick-buy-overwrite-yes';
    yesBtn.textContent = 'Yes';
    yesBtn.style.padding = '8px 20px';
    yesBtn.style.background = '#3a7bd5';
    yesBtn.style.color = '#fff';
    yesBtn.style.border = 'none';
    yesBtn.style.borderRadius = '4px';
    yesBtn.style.cursor = 'pointer';
    yesBtn.style.fontSize = '14px';

    const noBtn = document.createElement('button');
    noBtn.className = 'quick-buy-overwrite-no';
    noBtn.textContent = 'No';
    noBtn.style.padding = '8px 20px';
    noBtn.style.background = '#444';
    noBtn.style.color = '#fff';
    noBtn.style.border = 'none';
    noBtn.style.borderRadius = '4px';
    noBtn.style.cursor = 'pointer';
    noBtn.style.fontSize = '14px';

    const close = (): void => {
      if (overlay.parentNode) {
        overlay.parentNode.removeChild(overlay);
      }
      this.activeOverwriteDialog = null;
    };

    yesBtn.addEventListener('click', () => {
      // Build the new loadout: copy current, replace target slot.
      const next = [...this.loadout];
      next[slot] = weaponId;
      this.saveLoadout(next);
      close();
    });
    noBtn.addEventListener('click', close);
    // Tapping the dimmed backdrop cancels — matches standard mobile dialog UX.
    overlay.addEventListener('click', (ev) => {
      if (ev.target === overlay) close();
    });

    buttonRow.appendChild(yesBtn);
    buttonRow.appendChild(noBtn);
    popup.appendChild(buttonRow);
    overlay.appendChild(popup);
    document.body.appendChild(overlay);

    this.activeOverwriteDialog = overlay;
  }
}
